import { Star } from "lucide-react";

import { Hint } from "@/design-system/Hint.tsx";
import { TRACKED } from "@/map/pill-icons.ts";
import { toggleTracked, useTrackedListings } from "@/listings/listing.tracked.ts";
import { cn } from "@/design-system/cn.ts";

/**
 * Put this listing on the shortlist, or take it off.
 *
 * The map is the point of this, not the button: a tracked listing's pin takes
 * the shortlist colour, so the answer to "where were the three I liked?" is
 * visible from a whole-city view instead of being something you have to
 * reconstruct from open tabs.
 *
 * The star is filled in that same colour rather than the accent, so the control
 * and the mark it makes are recognisably one thing.
 */
export function TrackButton({ id, className }: { id: string; className?: string }) {
  const tracked = useTrackedListings();
  const on = tracked.has(id);

  return (
    <Hint
      label={on ? "On your shortlist" : "Track this listing"}
      detail={on ? "Click to remove it." : "Marks its pin on the map so you can find it again."}
      side="top"
    >
      <button
        type="button"
        onClick={() => toggleTracked(id)}
        aria-pressed={on}
        aria-label={on ? "Remove from shortlist" : "Add to shortlist"}
        className={cn(
          "grid shrink-0 place-items-center rounded-lg border border-rule bg-ink-900/85 px-3 py-2.5 text-chalk-dim transition",
          "hover:border-rule-bright hover:text-chalk active:scale-95",
          "focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-signal-ink",
          className,
        )}
        style={on ? { borderColor: TRACKED, color: TRACKED } : undefined}
      >
        <Star className="size-4" fill={on ? TRACKED : "none"} />
      </button>
    </Hint>
  );
}
